// Contratos Zod del chat de Jido (AI Copilot) — POST /api/jido/chat (F1b).
//
// Request: mensaje del usuario + historial opcional (máx 16 turnos de hasta
// 4000 caracteres — acota tokens de entrada, ver diseño §6/§7). Antes eran 10
// turnos × 2000 chars: las respuestas de Jido se cortaban y el modelo perdía
// el hilo en seguimientos cortos ("y el turno 3?"). Response: respuesta del asistente +
// metadata de ejecución (tools usadas, si se truncó por el cap de
// iteraciones del agente).
//
// SEGURIDAD: este contrato NUNCA acepta tenant_id/user_id/rol — la identidad
// sale SIEMPRE del JWT verificado (ver session.ts, RequestContext). Zod hace
// `strip` de claves desconocidas por default, así que cualquier campo de
// identidad que un cliente intente inyectar en el body es descartado antes
// de llegar al handler (ADR-J3 — la identidad nunca viaja como input).
import { z } from 'zod';
export const JIDO_HISTORY_MAX_TURNOS = 16;
export const JIDO_HISTORY_MAX_CHARS = 4000;
export const jidoChatTurnoSchema = z.object({
    role: z.enum(['user', 'assistant']),
    content: z.string().min(1).max(JIDO_HISTORY_MAX_CHARS),
});
export const jidoChatRequestSchema = z.object({
    message: z.string().min(1).max(2000),
    history: z.array(jidoChatTurnoSchema).max(JIDO_HISTORY_MAX_TURNOS).optional(),
});
// --- Anatomía de dato (rediseño FE, "confianza en el número") ---------------
//
// Un "dato destacado" que el FE pinta con jerarquía fija: número protagonista
// (Geist Mono) + chips de contexto + fuente. CLAVE: el `valor` y los `chips`
// los emite la TOOL desde la BD (ver jido/tools/*), NUNCA el modelo re-tipeando
// prosa — así el número grande es imposible de alucinar. `core.ts` los cosecha
// de los ToolMessage del run ReAct.
/** Chip de contexto discreto — "período 7-9 jul", "planta Sopó", "turnos 6 de 6". */
export const jidoChipSchema = z.object({
    etiqueta: z.string(),
    valor: z.string(),
});
export const jidoDatoSchema = z.object({
    /** Número/valor protagonista, ya formateado para mostrar — "93.4%", "$ 12.4 M". */
    valor: z.string(),
    /** Qué es el número — "cumplimiento", "valor producido". */
    etiqueta: z.string().optional(),
    /** Línea secundaria bajo el número — "meta 90% · +3.4 pts". */
    detalle: z.string().optional(),
    /**
     * Contexto rastreable del dato (período, planta, turnos, …). Requerido (array,
     * puede ser vacío) a propósito: NO usamos `.default([])` porque hace divergir
     * el tipo de input (chips opcional) del de output (chips requerido), y eso
     * rompe el build de producción del FE (`tsc -b` resuelve schemas por el dist).
     * La tool que emite el dato siempre pasa `chips` (aunque sea []).
     */
    chips: z.array(jidoChipSchema),
    /** Origen del dato para el affordance "ver fuente" — "OEE / órdenes de producción". */
    fuente: z.string().optional(),
});
export const jidoChatResponseSchema = z.object({
    reply: z.string(),
    // Aditivo y opcional: `undefined`/`[]` = comportamiento actual (solo prosa
    // markdown). Cuando viene poblado, el FE renderiza la anatomía de dato encima
    // del texto. Las tools que aún no emiten `datoDestacado` no aportan bloques.
    datos: z.array(jidoDatoSchema).optional(),
    used_tools: z.array(z.string()),
    truncated: z.boolean(),
});
//# sourceMappingURL=jido.js.map